import { useEffect, lazy, Suspense } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import { LangProvider, useLang } from "@/i18n";
import { isEnPath, toEnPath } from "@/langRouting";
import { SiteSettingsProvider, SegmentProvider } from "@/settings";
import { ThemeProvider } from "@/theme";
import { useSeo, setJsonLd, removeJsonLd } from "@/hooks/useSeo";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { PromoTiles, CampaignTile } from "@/components/PromoTiles";
import { BIREYSEL_ROUTES } from "@/bireyselRoutes";
import { SupportChat } from "@/components/SupportChat";
import { LangBanner } from "@/components/LangBanner";

// Rota bazlı kod bölme: ana sayfa dışındaki sayfalar ilk yüklemeyi şişirmez
const DynamicPage = lazy(() => import("@/components/DynamicPage"));
const ModulesPage = lazy(() => import("@/components/ModulesPage"));
const HomePlusPage = lazy(() => import("@/components/HomePlusPage"));
const KitapcikPage = lazy(() => import("@/components/KitapcikPage"));
const DestekPage = lazy(() => import("@/components/DestekPage"));
const PackagePage = lazy(() => import("@/components/PackagePage"));
const KarsilastirPage = lazy(() => import("@/components/KarsilastirPage"));
const BlogListPage = lazy(() => import("@/components/BlogListPage"));
const BlogPostPage = lazy(() => import("@/components/BlogPostPage"));
const ParasyoPlusCampaign = lazy(() => import("@/components/ParasyoPlusCampaign"));
const BireyselPage = lazy(() => import("@/components/BireyselPage"));
const RehberPage = lazy(() => import("@/components/RehberPage"));
const PosPage = lazy(() => import("@/components/PosPage"));
const PlanFinderPage = lazy(() => import("@/components/PlanFinderPage"));
const AdminLogin = lazy(() => import("@/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/admin/AdminLayout"));

// Fold altı ana sayfa bölümleri: ilk JS paketinden ayrı yüklenir (mobil hız)
const AdvantagesGrid = lazy(() => import("@/components/AdvantagesGrid").then((m) => ({ default: m.AdvantagesGrid })));
const Pricing = lazy(() => import("@/components/Pricing").then((m) => ({ default: m.Pricing })));
const Blog = lazy(() => import("@/components/Blog").then((m) => ({ default: m.Blog })));
const Faq = lazy(() => import("@/components/Faq").then((m) => ({ default: m.Faq })));
const Footer = lazy(() => import("@/components/Footer").then((m) => ({ default: m.Footer })));

const RouteFallback = () => <div className="App site-shell min-h-screen bg-operofy-dark" data-testid="route-fallback" />;

// Herkese açık rotalar: bare = TR, /en aynası = EN (LangSync dili URL'den yönetir)
const PUBLIC_ROUTES = [
  { path: "/", el: <Landing /> },
  { path: "/moduller", el: <ModulesPage /> },
  { path: "/home-plus", el: <HomePlusPage /> },
  { path: "/kitapcik", el: <KitapcikPage /> },
  { path: "/destek", el: <DestekPage /> },
  { path: "/paket/:slug", el: <PackagePage /> },
  { path: "/karsilastir", el: <KarsilastirPage /> },
  { path: "/hangi-paket", el: <PlanFinderPage /> },
  { path: "/blog", el: <BlogListPage /> },
  { path: "/blog/:slug", el: <BlogPostPage /> },
  { path: "/parasyo-plus", el: <ParasyoPlusCampaign /> },
  { path: "/pos", el: <PosPage /> },
  { path: "/eklentiler", el: <Navigate to="/moduller" replace /> },
  { path: "/eklentiler/:slug", el: <Navigate to="/moduller" replace /> },
  { path: "/rehber", el: <RehberPage /> },
  { path: "/sayfa/:slug", el: <DynamicPage /> },
  ...BIREYSEL_ROUTES.map((path) => ({ path, el: <BireyselPage pagePath={path} /> })),
];

// URL'yi dilin tek doğrusu yapar: /en → İngilizce; bare → TR.
// IP bazlı otomatik yönlendirme YOK (SEO: Googlebot kökte sabit TR içerik görür);
// yabancı ziyaretçiye LangBanner ile dil önerisi gösterilir. Yalnızca kullanıcının
// bilinçli dil tercihi (pref=en) hatırlanır ve /en aynasına taşınır.
function LangSync() {
  const { lang, setLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/admin")) return;
    if (isEnPath(path)) {
      document.documentElement.lang = "en";
      if (lang !== "en") setLang("en");
      return;
    }
    document.documentElement.lang = "tr";
    let pref = null;
    try { pref = localStorage.getItem("parasyo-lang"); } catch { /* yoksay */ }
    if (pref === "en") {
      navigate(toEnPath(path), { replace: true });
      return;
    }
    if (lang !== "tr") setLang("tr");
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function HashScroller() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      // Hash yoksa sayfa geçişinde her zaman en üstten başla
      if (window.__parasyoLenis) window.__parasyoLenis.scrollTo(0, { immediate: true, force: true });
      window.scrollTo(0, 0);
      return undefined;
    }
    let timer;
    let attempts = 0;
    const scroll = () => {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        const headerOffset = document.querySelector("header")?.offsetHeight || 0;
        const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerOffset - 12);
        if (window.__parasyoLenis) window.__parasyoLenis.scrollTo(top, { immediate: true, force: true });
        else window.scrollTo(0, top);
      }
      attempts += 1;
      if (attempts < 24) timer = setTimeout(scroll, 80);
    };
    timer = setTimeout(scroll, 80);
    return () => clearTimeout(timer);
  }, [pathname, hash]);

  return null;
}

function Landing() {
  useSeo("home");
  // Ana sayfa: SoftwareApplication şeması (Google uygulama kartı)
  useEffect(() => {
    setJsonLd("software-app", {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Parasyo",
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web, Android, iOS",
      url: "https://parasyo.com/",
      description:
        "Kişisel bütçe, ev (Home+) ve işletme finansını tek uygulamada toplayan finans asistanı. Küçük işletmeler için ücretsiz ön muhasebe: gelir-gider takibi, stok, teklif ve raporlar.",
      inLanguage: ["tr", "en"],
      author: { "@id": "https://parasyo.com/#organization" },
      offers: [
        { "@type": "Offer", name: "ParasyoFree", price: "0", priceCurrency: "TRY" },
        { "@type": "Offer", name: "Plus", price: "90.00", priceCurrency: "TRY" },
        { "@type": "Offer", name: "Premium", price: "299.00", priceCurrency: "TRY" },
        { "@type": "Offer", name: "Pro", price: "499.00", priceCurrency: "TRY" },
      ],
    });
    return () => removeJsonLd("software-app");
  }, []);
  return (
    <div className="App site-shell bg-operofy-dark">
      <Header />
      <main>
        <Hero />
        <CampaignTile />
        <PromoTiles />
        <Suspense fallback={null}>
          <Pricing />
          <AdvantagesGrid />
          <Blog />
          <Faq />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__parasyoLenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__parasyoLenis = null;
    };
  }, []);

  return (
    <LangProvider>
      <ThemeProvider>
      <SegmentProvider>
        <SiteSettingsProvider>
          <BrowserRouter>
        <HashScroller />
        <LangSync />
        <SupportChat />
        <LangBanner />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {PUBLIC_ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={r.el} />
          ))}
          {PUBLIC_ROUTES.map((r) => (
            <Route key={`en:${r.path}`} path={toEnPath(r.path)} element={r.el} />
          ))}
          <Route path="/fiyatlandirma" element={<Navigate to="/karsilastir" replace />} />
          <Route path="/en/fiyatlandirma" element={<Navigate to="/en/karsilastir" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />} />
        </Routes>
        </Suspense>
          </BrowserRouter>
        </SiteSettingsProvider>
      </SegmentProvider>
      </ThemeProvider>
      <Toaster position="bottom-right" richColors />
    </LangProvider>
  );
}

export default App;
